// stores/userStore.js
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Capacitor } from '@capacitor/core';
import * as userApi from '@/api/userApi.js';
import { usePermissions } from '@/composables/usePermissions';
import { getDeviceId } from '@/utils/deviceId';

const AuthCodes = {
  FAILED: -1,
  SUCCESS: 0,
  ALREADY_EXISTS: 2000,
  USERNAME_EXISTS: 2001,
  EMAIL_EXISTS: 2002,
  LOGIN_EXISTS: 2003,
  PHONE_EXISTS: 2004,
  USER_ACTIVE: 2015,
  USER_INACTIVE: 2016
};

// Уровни доступа для ролей
const AccessLevels = {
  'Owner': 100,
  'Admin': 80,
  'Manager': 60,
  'Editor': 40,
  'User': 20,
  'Guest': 10
};

const CryptoConfig = {
  algorithm: { name: 'AES-GCM', length: 256 },
  ivLength: 12
};

export const useUserStore = defineStore('user', () => {
  // State
  const authData = ref(null);
  const authStatus = ref(null);
  const accessRights = ref(null);
  const _recentProjects = ref(null);
  const isEmailSent = ref(false);
  const lastApiError = ref(null);
  const loading = ref(false);

  const hasAttemptedAutoLogin = ref(false);
  let autoLoginPromise = null;

  // Getters
  const isAuthenticated = computed(() => authStatus.value === AuthCodes.SUCCESS && !!authData.value);
  const isFreemium = computed(() => licenseTag.value === "freemium");
  const isLoading = computed(() => loading.value);
  const status = computed(() => authStatus.value);
  const user = computed(() => authData.value?.user || null);
  const data = computed(() => authData.value?.data || null);
  const token = computed(() => authData.value?.token || null);
  const recentProjects = computed(() => _recentProjects.value || []);
  const userShortName = computed(() => {
    if (!authData.value?.user) return null;
    return authData.value.user.first_name;
  });
  const userFullName = computed(() => {
    if (!authData.value?.user) return null;
    const { first_name, last_name } = authData.value.user;
    return [first_name, last_name].filter(Boolean).join(' ').trim();
  });
  const needsEmailVerification = computed(() => authStatus.value === AuthCodes.USER_INACTIVE);
  const errorMessage = computed(() => {
    switch (authStatus.value) {
      case AuthCodes.ALREADY_EXISTS: return 'Пользователь с такими параметрами уже существует';
      case AuthCodes.USERNAME_EXISTS: return 'Пользователь с таким именем уже существует';
      case AuthCodes.EMAIL_EXISTS: return 'Пользователь с таким email уже существует';
      case AuthCodes.LOGIN_EXISTS: return 'Пользователь с таким логином уже существует';
      case AuthCodes.PHONE_EXISTS: return 'Пользователь с таким телефоном уже существует';
      case AuthCodes.USER_ACTIVE: return 'Пользователь уже активен';
      case AuthCodes.USER_INACTIVE: return 'Требуется подтверждение email';
      case AuthCodes.FAILED: return 'Авторизация не удалась';
      default: return lastApiError.value || 'Произошла ошибка';
    }
  });

  // Геттеры для лицензий и ролей
  const license = computed(() => accessRights.value?.license || null);
  const licenseTag = computed(() => accessRights.value?.license?.tag || 'freemium');
  const licenseTitle = computed(() => accessRights.value?.license?.title || 'Freemium');
  const licenseFeatures = computed(() => accessRights.value?.license?.params?.templates || {});
  const licenseLimits = computed(() => accessRights.value?.license?.params?.limits || {});
  const roles = computed(() => {
    if (!accessRights.value || Object.keys(accessRights.value).length === 0) {
      return [];
    }
    return accessRights.value.roles || [];
  });
  const roleTitles = computed(() => {
    return Array.isArray(roles.value) ? roles.value.map(role => role.title) : []
  });
  const spaces = computed(() => {
    const spacesMap = {};
    
    if (!Array.isArray(roles.value)) return {};

    roles.value.forEach(role => {
      if (role.company_id && role.title) {
        spacesMap[role.company_id] = role.title;
      }
    });
    return spacesMap;
  });

  // Crypto methods
  async function _getCryptoKey() {
    const secret = "as876asdfjllkKJosx687HLAKJMCLAqr";

    return await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(secret),
      CryptoConfig.algorithm,
      false,
      ['encrypt', 'decrypt']
    );
  }

  async function _encryptData(data) {
    try {
      const key = await _getCryptoKey();
      const iv = crypto.getRandomValues(new Uint8Array(CryptoConfig.ivLength));
      const encrypted = await crypto.subtle.encrypt({
        ...CryptoConfig.algorithm,
        iv
      }, key, new TextEncoder().encode(JSON.stringify(data)));

      return JSON.stringify({
        iv: Array.from(iv),
        data: Array.from(new Uint8Array(encrypted))
      });
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('ENCRYPTION_FAILED');
    }
  }

  async function _decryptData(encryptedStr) {
    if (!encryptedStr) return null;

    try {
      const { iv, data } = JSON.parse(encryptedStr);
      const key = await _getCryptoKey();
      const decrypted = await crypto.subtle.decrypt({
        ...CryptoConfig.algorithm,
        iv: new Uint8Array(iv)
      }, key, new Uint8Array(data));

      return JSON.parse(new TextDecoder().decode(decrypted));
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }

  // Auth data methods
  async function _persistAuthData() {
    if (!authData.value) return;
    
    try {
      const encrypted = await _encryptData(authData.value);
      localStorage.setItem('authData', encrypted);
    } catch {
      _clearAuthData();
    }
  }

  async function _loadAuthData() {
    const stored = localStorage.getItem('authData');
    return stored ? await _decryptData(stored) : null;
  }

  function _clearAuthData() {
    authData.value = null;
    authStatus.value = null;
    localStorage.removeItem('authData');
  }

  async function registerDevice({ token: pushToken, platform }) {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative || !user.value?.id) {
      console.log('[userStore] registerDevice skipped (web or no user)');
      return;
    }

    try {
      const deviceId = await getDeviceId();
      
      const data = {
        'device_id': deviceId,
        'provider': platform,
        'platform': Capacitor.getPlatform(),
        'token': pushToken
      }
      
      let response = await _callSecureApi(userApi.registerDevice, data);

      if (!response) {
        throw new Error(`Failed to register device: ${response.ok}`);
      }
      
      console.log('[userStore] device registered');
    } catch (error) {
      console.error('[userStore] registerDevice failed:', error);
    }
  }

  async function unregisterDevice() {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative || !token.value) {
      console.log('[userStore] unregisterDevice skipped (web or no token)');
      return;
    }

    try {
      const deviceId = await getDeviceId();
      
      await _callSecureApi(userApi.unregisterDevice, deviceId);

      console.log('[userStore] device unregistered');
    } catch (error) {
      console.warn('[userStore] unregisterDevice failed:', error.message);
    }
  }

  // Public methods
  async function login({ login, password, code }) {
    try {
      const response = await userApi.login(login, password, code);

      if (response.token && response.data) {
        authData.value = {
          token: response.token,
          user: response.data,
          license: response.license,
          status: response.status,
          config: response.config,
          question: !response.question
        };
        authStatus.value = AuthCodes.SUCCESS;
        await _persistAuthData();
        
        accessRights.value = await _callSecureApi(userApi.fetchData);

        // Инициализация системы прав
        const permissions = usePermissions();
        await permissions.initialize({
          tariff: response.license
        });

        // 👉 PUSH: Push будет инициализирован в App.vue через watch на isAuthenticated
        // Не вызываем registerDevice здесь, чтобы избежать дублирования

        return { success: true };
      }

      authStatus.value = response.status || AuthCodes.FAILED;

      return {
        success: false,
        status: authStatus.value,
        code: response.code,
        message: errorMessage.value
      };
    } catch (error) {
      _clearAuthData();
      throw error;
    }
  }

  async function autoLogin() {
    if (hasAttemptedAutoLogin.value) {
      return autoLoginPromise || isAuthenticated.value;
    }

    hasAttemptedAutoLogin.value = true;

    autoLoginPromise = (async () => {
      try {
        const loadedAuthData = await _loadAuthData();
        if (!loadedAuthData?.token) {
          _clearAuthData();
          return false;
        }

        const response = await userApi.fetchData(loadedAuthData.token);
        if (!response?.id) {
          _clearAuthData();
          return false;
        }

        authData.value = {
          token: loadedAuthData.token,
          user: response.user || loadedAuthData.user,
          data: response.data || loadedAuthData.data,
          question: !response.question
        };
        authStatus.value = AuthCodes.SUCCESS;

        accessRights.value = response;

        // Инициализация системы прав
        const permissions = usePermissions();
        await permissions.initialize({
          tariff: response.license
        });

        await _persistAuthData();

        // 👉 PUSH: Push будет инициализирован в App.vue через watch на isAuthenticated

        return true;
      } catch (error) {
        _clearAuthData();
        return false;
      } finally {
        autoLoginPromise = null;
      }
    })();

    return autoLoginPromise;
  }

  async function register(userData) {
    try {
      const response = await userApi.register(userData);
      
      if (response.id) {
        isEmailSent.value = true;
        return { success: true, needsActivation: true };
      }

      authStatus.value = response.code;
      return { success: false, code: response.code };
    } catch (error) {
      lastApiError.value = error.message;
      throw error;
    }
  }

  async function activate(key) {
    try {
      const response = await userApi.activate(key);
      if (response && response.id) {
        isEmailSent.value = false;
        return true;
      } else {
        return false;
      }
    } catch (error) {
      lastApiError.value = error.message;
      throw error;
    }
  }

  async function resendCode(email) {
    try {
      const response = await userApi.resendCode(email);
      isEmailSent.value = true;
      return response;
    } catch (error) {
      lastApiError.value = error.message;
      throw error;
    }
  }

  async function restore(email) {
    try {
      await userApi.restore(email);
      return true;
    } catch (error) {
      lastApiError.value = error.message;
      throw error;
    }
  }

  // 👉 PUSH: logout стал async и вызывает unregisterDevice ПЕРЕД очисткой
  async function logout() {
    // 1️⃣ СНАЧАЛА отзываем устройство, пока есть token
    await unregisterDevice();

    // 2️⃣ Очистка системы прав
    const permissions = usePermissions();
    permissions.cleanup();

    // 3️⃣ ПОТОМ чистим состояние
    _clearAuthData();
    isEmailSent.value = false;
  }

  // Secure methods
  async function _callSecureApi(apiFn, ...args) {
    if (!token.value) throw new Error('Требуется авторизация');

    loading.value = true;

    try {
      const response = await apiFn(token.value, ...args);    
      loading.value = false;
      return response;
    } catch (error) {
      if (error.response?.status === 401) _clearAuthData();
      lastApiError.value = error.message;
      loading.value = false;
      throw error;
    }
  }

  async function info() {
    const response = await _callSecureApi(userApi.userInfo, null);
    if (response && authData.value?.user) {
      await _persistAuthData();
    }
    return response;
  }

  async function refresh() {
    const response = await _callSecureApi(userApi.fetchData);
    if (!response) return null;
    accessRights.value = response;
    if (authData.value) {
      authData.value.question = !response.question;
      if (response.user) authData.value.user = response.user;
    }
    await _persistAuthData();
    return response;
  }

  async function uploadAvatar(data) {
    const response = await _callSecureApi(userApi.uploadAvatar, data);
    if (response && authData.value?.user) {
      authData.value.user.imageFile = response.imageFile;
      await _persistAuthData();
    }
    return response;
  }

  async function removeAvatar(data) {
    const response = await _callSecureApi(userApi.removeAvatar, data);
    if (response && authData.value?.user) {
      authData.value.user.imageFile = null;
      await _persistAuthData();
    }
    return response;
  }

  async function fetchRecent() {
    const response = await _callSecureApi(userApi.recentProjects);
    _recentProjects.value = response;
  }

  async function changePassword(passwordData) {
    return _callSecureApi(userApi.changePassword, passwordData);
  }

  async function saveSettings(settings) {
    const response = await _callSecureApi(userApi.saveSettings, settings);
    if (response && authData.value?.user) {
      authData.value.user = { ...authData.value.user, ...response.updatedUser };
      await _persistAuthData();
    }
    return response;
  }

  // Новые геттеры для проверки прав
  const hasRole = (roleTitle) => computed(() => {
    return roleTitles.value.includes(roleTitle);
  });

  const hasAnyRole = (roleTitlesArray) => computed(() => {
    if (!Array.isArray(roleTitlesArray)) return false;
    return roleTitlesArray.some(role => roleTitles.value.includes(role));
  });

  const hasSpaceRole = (spaceId, roleTitle) => computed(() => {
    const role = roles.value.find(r => r.company_id === spaceId);
    return role?.title === roleTitle;
  });

  const hasLicense = (licenseTagValue) => computed(() => {
    return licenseTag.value === licenseTagValue;
  });

  const hasAnyLicense = (licenseTags) => computed(() => {
    if (!Array.isArray(licenseTags)) return false;
    return licenseTags.includes(licenseTag.value);
  });

  const hasFeature = (featureName) => computed(() => {
    const feature = licenseFeatures.value[featureName];
    if (!feature) return false;
    
    if (feature.type === 'boolean') {
      return feature.value === true || feature.value === 'true';
    }
    if (feature.type === 'enum') {
      return feature.value !== 'false' && feature.value !== false;
    }
    return !!feature.value;
  });

  const getFeatureValue = (featureName) => computed(() => {
    const feature = licenseFeatures.value[featureName];
    return feature?.value;
  });

  const checkLimit = (limitName, valueToCheck = 1) => computed(() => {
    const limit = licenseLimits.value[limitName];
    if (!limit) return true;
    
    const limitValue = parseFloat(limit.value);
    const currentValue = parseFloat(valueToCheck);
    
    return currentValue <= limitValue;
  });

  const maxAccessLevel = computed(() => {
    const userRoles = roleTitles.value || [];
    const AccessLevels = {
      'Owner': 100,
      'Admin': 80,
      'Manager': 60,
      'Editor': 40,
      'User': 20,
      'Guest': 10
    };
    
    return Math.max(...userRoles.map(role => AccessLevels[role] || 0));
  });

  const hasAccessLevel = (minLevel) => computed(() => {
    return maxAccessLevel.value >= minLevel;
  });

  return {
    // State
    authData,
    authStatus,
    isEmailSent,
    lastApiError,
    
    // Getters
    isAuthenticated,
    isFreemium,
    status,
    user,
    data,
    token,
    userShortName,
    userFullName,
    needsEmailVerification,
    errorMessage,
    recentProjects,
    
    // Actions
    login,
    autoLogin,
    register,
    activate,
    resendCode,
    restore,
    logout,
    info,
    refresh,
    uploadAvatar,
    removeAvatar,
    fetchRecent,
    changePassword,
    saveSettings,

    // 👉 PUSH: Новые методы
    registerDevice,
    unregisterDevice,

    // Новые геттеры для прав
    license,
    licenseTag,
    licenseTitle,
    licenseFeatures,
    licenseLimits,
    roles,
    roleTitles,
    spaces,
    hasRole,
    hasAnyRole,
    hasSpaceRole,
    hasLicense,
    hasAnyLicense,
    hasFeature,
    getFeatureValue,
    checkLimit,
    maxAccessLevel,
    hasAccessLevel    
  };
});